import { memo, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { IMenuByTypesItem } from '@r1-frontend/api-domru/api-content/v2/menu/by-types/dto/IMenuByTypesItem';

import { Overlay } from '@r1-frontend/ui-react/components/overlay/';
import ToolTip from '@r1-frontend/ui-react/components/tooltip/index';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';
import { BoxShadow } from '@r1-frontend/ui-react/tokens/boxShadow';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import * as ST from './styled';

interface IProps {
    service: IMenuByTypesItem,
    onDataLayerHandler: (menuType: 'Main' | 'Right', title: string) => void,
}

const ServiceMenuItem = ({ service, onDataLayerHandler }: IProps): JSX.Element => {
    const [currentChild] = service.children || [];
    const router = useRouter();

    const substrateRef = useRef<HTMLDivElement>(null);

    const [isCollapsed, setIsCollapsed] = useState(false);
    const withDropDown = useMemo(() => !!currentChild?.children?.length, [currentChild]);

    return (
        <ST.MenuItemWrapper
            withDropDown={withDropDown}
            isCollapsed={isCollapsed}
            onClick={() => setIsCollapsed(!isCollapsed)}
        >
            {withDropDown
                ? <ST.ItemText ref={substrateRef}>{service.title}</ST.ItemText>
                : <Link
                    href={service.link}
                    prefetch={false}
                    passHref
                >
                    <ST.ItemLink>{service.title}</ST.ItemLink>
                </Link>
            }
            {withDropDown && (
                <Overlay isOpen={isCollapsed} onClose={() => setIsCollapsed(false)}>
                    <ToolTip
                        active={isCollapsed}
                        parent={substrateRef.current}
                        position='bottom'
                        arrow='right'
                        align='right'
                        customStyles={{
                            style: {
                                padding: '16px',
                                borderRadius: BorderRadius.componentRadius,
                                backgroundColor: COLORS.BgMain,
                                boxShadow: BoxShadow.M,
                                zIndex: 1000,
                            },
                        }}
                    >
                        <ST.Ul data-test='service-dropdown'>
                            {currentChild.children?.map(child => (
                                <li
                                    key={`desktop-dropdown-${child.id}`}
                                    onClick={() => {
                                        router.push(child.link);
                                        onDataLayerHandler('Right', child.title);
                                    }}
                                    data-test='dropdown-item'
                                >
                                    <ST.DropDownLink>{child.title}</ST.DropDownLink>
                                </li>
                            ))}
                        </ST.Ul>
                    </ToolTip>
                </Overlay>
            )}
        </ST.MenuItemWrapper>
    );
};

export default memo(ServiceMenuItem);
